// Authentication service for handling tokens and authenticated API calls
class AuthService {
  constructor() {
    this.baseHeaders = {
      'Content-Type': 'application/json',
    };
  }

  // Get the current access token from localStorage
  getAccessToken() {
    return localStorage.getItem('accessToken');
  }

  // Get the current refresh token from localStorage
  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  // Get the current ID token from localStorage
  getIdToken() {
    return localStorage.getItem('idToken');
  }

  // Check if user is authenticated
  isAuthenticated() {
    const token = this.getAccessToken();
    const isAuth = localStorage.getItem('isAuthenticated');
    return token && isAuth === 'true';
  }

  // Get authenticated headers for API calls
  getAuthHeaders(additionalHeaders = {}) {
    const token = this.getAccessToken();
    return {
      ...this.baseHeaders,
      ...additionalHeaders,
      ...(token && { 'Authorization': `Bearer ${token}` }),
    };
  }

  // Make an authenticated API call
  async authenticatedFetch(url, options = {}) {
    const token = this.getAccessToken();
    
    if (!token) {
      throw new Error('No access token available');
    }

    const headers = {
      ...this.baseHeaders,
      ...options.headers,
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    // Handle token expiration
    if (response.status === 401 || response.status === 403) {
      // Token might be expired, try to refresh
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the request with new token
        const newToken = this.getAccessToken();
        headers.Authorization = `Bearer ${newToken}`;
        return await fetch(url, {
          ...options,
          headers,
        });
      } else {
        // Refresh failed, redirect to login
        this.logout();
        throw new Error('Authentication failed');
      }
    }

    return response;
  }

  // Refresh the access token using refresh token
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${process.env.REACT_APP_USER_API_URL}/users/refresh-token`, {
        method: 'POST',
        headers: this.baseHeaders,
        body: JSON.stringify({
          refreshToken: refreshToken,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.data && data.data.accessToken) {
          localStorage.setItem('accessToken', data.data.accessToken);
          if (data.data.refreshToken) {
            localStorage.setItem('refreshToken', data.data.refreshToken);
          }
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  // Refresh the access token when it expires within `minValiditySeconds`, read
  // from the JWT's own `exp`. For calls that can't react to a 401 — DDS answers
  // an expired token with an empty competition list rather than an error.
  // Concurrent callers share one refresh.
  async ensureFreshAccessToken(minValiditySeconds = 60) {
    const token = this.getAccessToken();
    if (!token) return;
    let exp;
    try {
      exp = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/'))).exp;
    } catch {
      return;
    }
    if (!exp || exp - Date.now() / 1000 > minValiditySeconds) return;
    if (!this.pendingRefresh) {
      this.pendingRefresh = this.refreshToken().finally(() => { this.pendingRefresh = null; });
    }
    await this.pendingRefresh;
  }

  // Logout and clear all tokens
  logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('tokenExpiresIn');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userData');
    
    // Redirect to login page
    window.location.href = '/login';
  }

  // Store tokens after successful login
  storeTokens(tokens) {
    if (tokens.accessToken) {
      localStorage.setItem('accessToken', tokens.accessToken);
    }
    if (tokens.refreshToken) {
      localStorage.setItem('refreshToken', tokens.refreshToken);
    }
    if (tokens.idToken) {
      localStorage.setItem('idToken', tokens.idToken);
    }
    if (tokens.expiresIn) {
      localStorage.setItem('tokenExpiresIn', tokens.expiresIn);
    }
  }

  // Check if token is expired
  isTokenExpired() {
    const expiresIn = localStorage.getItem('tokenExpiresIn');
    if (!expiresIn) return true;
    
    const expiryTime = parseInt(expiresIn);
    const currentTime = Math.floor(Date.now() / 1000);
    
    return currentTime >= expiryTime;
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService; 